import axios, { AxiosRequestConfig } from 'axios';
// eslint-disable-next-line no-restricted-imports
import { config } from 'shared/lib';
import BaseError from 'utils/base-error';
import { z } from 'zod';

export type ApiResponse<T = any> = {
    data: T
    requestParams: object
}

interface IApi {
    url: string

    get<T = any>(
        path: string,
        params?: object,
        responseShema?: z.ZodTypeAny
    ): Promise<ApiResponse<T> | BaseError>
}

const cacheMap = new Map();

export class Api implements IApi {
    url: string;

    apiToken: string;

    constructor(
        url: string,
        apiToken: string,
    ) {
        this.url = url;
        this.apiToken = apiToken;
    }

    async get<T = any>(
        path: string,
        params?: object,
        responseShema?: z.ZodTypeAny,
    ): Promise<ApiResponse<T> | BaseError> {
        if (cacheMap.has(`${path}@${JSON.stringify(params)}`)) {
            return cacheMap.get(`${path}@${JSON.stringify(params)}`);
        }
        const config: AxiosRequestConfig = {
            method: 'GET',
            url: this.url + path,
            headers: {
                Authorization: `Bearer ${this.apiToken}`,
            },
            params: {
                'pagination[pageSize]': 100,
                ...params,
            },
        };

        try {
            const { status, statusText, data } = await axios(config);
            if ([200, 201].includes(status)) {
                if (responseShema) {
                    const parcedResponse = responseShema.parse(data);

                    const result = {
                        data: parcedResponse,
                        requestParams: {
                            path,
                            params,
                        },
                    };

                    cacheMap.set(
                        `${path}@${JSON.stringify(params)}`,
                        result,
                    );
                    return result;
                }
                const result = {
                    data,
                    requestParams: {
                        path,
                        params,
                        responseShema,
                    },
                };

                cacheMap.set(
                    `${path}@${JSON.stringify(params)}`,
                    result,
                );
                return result;
            }
            return new BaseError(status, statusText);
        } catch (e) {
            if (e instanceof z.ZodError) return new BaseError(500, e.message);
            return new BaseError(500, String(e));
        }
    }

    async post<T = any>(
        path: string,
        data?: any,
        params?: object,
        headers?: object,
        responseShema?: z.ZodTypeAny,
    ): Promise<ApiResponse<T> | BaseError> {
        if (cacheMap.has(`${path}@${JSON.stringify(params)}`)) {
            return cacheMap.get(`${path}@${JSON.stringify(params)}`);
        }
        const axiConfig: AxiosRequestConfig = {
            method: 'POST',
            url: this.url + path,
            headers: {
                Authorization: `Bearer ${this.apiToken}`,
            },
            data,
            params,
        };

        try {
            const { status, statusText, data } = await axios(axiConfig);
            if ([200, 201].includes(status)) {
                if (responseShema) {
                    const parcedResponse = responseShema.parse(data);

                    const result = {
                        data: parcedResponse,
                        requestParams: {
                            path,
                            params,
                        },
                    };
                    return result;
                }
                const result = {
                    data,
                    requestParams: {
                        path,
                        params,
                        responseShema,
                    },
                };

                return result;
            }
            return new BaseError(status, statusText);
        } catch (e) {
            if (e instanceof z.ZodError) return new BaseError(500, e.message);
            return new BaseError(500, String(e));
        }
    }
}

export const ApiFactory = (() => {
    let instance: Api | null = null;
    return {
        getInstance: () => {
            if (instance === null) {
                instance = new Api(
                    config.strapiUrl + config.strapiPrefix,
                    config.strapi_token,
                );
            }
            return instance;
        },
    };
})();

export const api = new Api(config.strapiUrl + config.strapiPrefix, config.strapi_token);
